import { useEffect, useRef, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { datePlaceholder, formatDate, maskDateInput, parseDate } from '../utils/dateFormat';

/**
 * A date box that shows and takes dates the way the organization writes them (18/10/2026 for India), whatever the browser's own
 * language is. It stands in for <input type="date">: the value is YYYY-MM-DD, and onChange gets { target: { name, value } } like any
 * input, so the form code around it is unchanged. Type it (separators go in as you type) or press the calendar for the browser's picker.
 * min and max (YYYY-MM-DD) refuse dates outside them, as the native box did.
 */
function DateInput({ value, onChange, min, max, required, disabled, name, id, className, style, size, placeholder, autoFocus, onBlur }) {
  const { dateFormat } = useAuth() || {};
  const format = dateFormat || 'DD/MM/YYYY';
  const [text, setText] = useState(formatDate(value, format));
  const [typing, setTyping] = useState(false);
  const picker = useRef(null);
  const box = useRef(null);

  // Show what the form holds, unless the person is in the middle of typing
  useEffect(() => {
    if (!typing) setText(formatDate(value, format));
  }, [value, format, typing]);

  const outOfRange = (iso) => (min && iso < min) || (max && iso > max);
  const iso = parseDate(text, format);
  const bad = text !== '' && (iso === '' || outOfRange(iso));

  useEffect(() => {
    if (box.current) {
      box.current.setCustomValidity(bad ? (iso && outOfRange(iso) ? 'This date is not allowed here' : `Enter a date as ${datePlaceholder(format)}`) : '');
    }
  });

  const emit = (v) => onChange && onChange({ target: { name, value: v } });

  const handleText = (e) => {
    const masked = maskDateInput(e.target.value, format);
    setText(masked);
    setTyping(true);
    if (masked === '') {
      emit('');
      return;
    }
    const parsed = parseDate(masked, format);
    if (parsed && !outOfRange(parsed)) emit(parsed);
  };

  const handleBlur = (e) => {
    setTyping(false);
    // What cannot be read as an allowed date goes back to what the form holds
    if (bad) setText(formatDate(value, format));
    if (onBlur) onBlur(e);
  };

  const openPicker = () => {
    const el = picker.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch (err) {
        // Falls through to focusing it
      }
    }
    el.focus();
    el.click();
  };

  return (
    <InputGroup size={size} className={`flex-nowrap ${className || ''}`} style={{ minWidth: '8.75rem', ...style }}>
      <Form.Control
        ref={box}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        value={text}
        placeholder={placeholder || datePlaceholder(format)}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        isInvalid={bad && typing}
        onChange={handleText}
        onBlur={handleBlur}
        maxLength={10}
      />
      <Button variant="outline-secondary" type="button" disabled={disabled} onClick={openPicker} aria-label="Choose from a calendar" tabIndex={-1}>
        <i className="bi bi-calendar3" aria-hidden="true" />
      </Button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => { setTyping(false); setText(formatDate(e.target.value, format)); emit(e.target.value); }}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0, border: 0, padding: 0 }}
      />
    </InputGroup>
  );
}

export default DateInput;
